<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="md:tw-px-8 tw-pt-6 tw-pb-10">

        <v-btn text color="primary" class="tw-mb-4" @click="$router.push({ name: 'AgentRenewals' })">
          <v-icon left>mdi-arrow-left</v-icon>
          Back to Renewals
        </v-btn>

        <div v-if="loading" class="tw-w-full tw-flex tw-justify-center tw-py-20">
          <v-progress-circular :size="56" color="#00AEEF" indeterminate />
        </div>

        <template v-else-if="renewal && renewal.policyNo">
          <header class="page-header tw-mb-6">
            <div>
              <h2 class="page-title">Renewal Details</h2>
            </div>
            <!-- Temporarily disabled: renewals start from details
            <v-btn
              v-if="showRenewNow"
              color="primary"
              large
              depressed
              class="renew-now-btn"
              @click="startRenewal"
            >
              <v-icon left>mdi-refresh</v-icon>
              Renew Now
            </v-btn>
            -->
          </header>

          <!-- Renewal Info Card -->
          <div class="detail-card tw-mb-6">
            <h3 class="detail-section-title">Renewal Information</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Client</span>
                <span class="detail-value">{{ renewal.clientName || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Product</span>
                <span class="detail-value">{{ renewal.product || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Policy No</span>
                <span class="detail-value">{{ renewal.policyNo || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Renewal Date</span>
                <span class="detail-value">{{ formatDate(renewal.renewalDate) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Current Premium</span>
                <span class="detail-value">{{ formatCurrency(renewal.premium) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Renewal Premium</span>
                <span class="detail-value">{{ formatCurrency(renewal.futurePremium) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Sum Insured</span>
                <span class="detail-value">{{ formatCurrency(renewal.sumInsured) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Commission</span>
                <span class="detail-value">{{ formatCurrency(renewal.commissionAmount) }}</span>
              </div>
              <div class="detail-item" v-if="renewal.effectiveBatchNo">
                <span class="detail-label">Batch No</span>
                <span class="detail-value">{{ renewal.effectiveBatchNo }}</span>
              </div>
              <div class="detail-item" v-if="renewal.yourRef">
                <span class="detail-label">Your Ref</span>
                <span class="detail-value">{{ renewal.yourRef }}</span>
              </div>
            </div>
          </div>

          <!-- Risks Table -->
          <div class="detail-card tw-mb-6">
            <h3 class="detail-section-title">Risks</h3>
            <div v-if="risks.length > 0" class="risks-table-wrap">
              <table class="risks-table">
                <thead>
                  <tr>
                    <th>Risk ID</th>
                    <th>Description</th>
                    <th>Value</th>
                    <th>Sub Class</th>
                    <th>Cover Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, idx) in risks" :key="idx" class="risks-row">
                    <td>{{ r.riskId || '—' }}</td>
                    <td>{{ r.riskDesc || '—' }}</td>
                    <td>{{ formatCurrency(r.riskValue) }}</td>
                    <td>{{ r.subClass || '—' }}</td>
                    <td>{{ r.coverType || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-risks">
              <v-icon size="48" color="#CBD5E1">mdi-shield-outline</v-icon>
              <p>No risks recorded for this renewal.</p>
            </div>
          </div>

          <div class="detail-card tw-mb-6">
            <h3 class="detail-section-title">Benefits</h3>
            <p class="benefits-hint">
              {{ isComprehensive
                ? 'What is currently on this policy. You can add or remove extras when you renew.'
                : 'Statutory third-party cover. Optional extras can be changed on a comprehensive renewal.' }}
            </p>
            <div v-if="benefitRows.length" class="benefit-stack">
              <div
                v-for="(b, idx) in benefitRows"
                :key="`${b.sectCode || b.name}-${idx}`"
                class="benefit-stack-item"
              >
                <div class="benefit-stack-copy">
                  <span class="benefit-stack-name">{{ formatBenefitName(b.name) }}</span>
                  <span v-if="b.riskLabel" class="benefit-stack-vehicle">{{ b.riskLabel }}</span>
                </div>
                <span class="benefit-stack-meta">{{ benefitLimitLabel(b) }}</span>
              </div>
            </div>
            <div v-else class="empty-risks">
              <v-icon size="48" color="#CBD5E1">mdi-shield-plus-outline</v-icon>
              <p>No additional benefits recorded on this policy.</p>
            </div>
          </div>

          <!-- Policy Documents Section -->
          <div class="detail-card tw-mt-6">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-5"
                 style="padding-bottom:12px; border-bottom:2px solid #E8EDF2;">
              <h3 class="detail-section-title tw-mb-0" style="border:none; padding:0;">
                Policy Documents
              </h3>
              <span v-if="!policyDocsLoading && policyDocs.length > 0" class="docs-count-badge">
                {{ policyDocs.length }} file{{ policyDocs.length !== 1 ? 's' : '' }}
              </span>
            </div>

            <div v-if="policyDocsLoading" class="tw-flex tw-items-center tw-gap-3 tw-py-8 tw-justify-center">
              <v-progress-circular indeterminate color="#00AEEF" size="28"></v-progress-circular>
              <span style="color:#94A3B8; font-size:0.875rem;">Loading documents…</span>
            </div>
            <div v-else-if="policyDocsError" class="empty-risks">
              <v-icon size="40" color="#FC8181">mdi-alert-circle-outline</v-icon>
              <p>{{ policyDocsError }}</p>
            </div>
            <div v-else-if="policyDocs.length === 0" class="empty-risks">
              <v-icon size="48" color="#CBD5E1">mdi-folder-open-outline</v-icon>
              <p>No documents found for this policy.</p>
            </div>
            <div v-else class="policy-docs-grid">
              <div
                v-for="doc in policyDocs"
                :key="doc.id"
                class="policy-doc-card"
                @click="openDocument(doc)"
              >
                <div class="policy-doc-icon-wrap">
                  <v-icon size="28" :color="doc.mimeType === '.pdf' ? '#E53E3E' : '#718096'">
                    {{ doc.mimeType === '.pdf' ? 'mdi-file-pdf-box' : 'mdi-file-document-outline' }}
                  </v-icon>
                </div>
                <div class="policy-doc-body">
                  <p class="policy-doc-name">{{ doc.actualName }}</p>
                  <span class="policy-doc-type">{{ doc.docType }}</span>
                </div>
                <div class="policy-doc-footer">
                  <span class="policy-doc-date">{{ doc.dateCreated }}</span>
                  <v-btn x-small depressed color="primary">
                    <v-icon x-small left>mdi-eye-outline</v-icon> View
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Not Found -->
        <div v-else class="empty-state tw-py-16">
          <v-icon size="60" color="#CBD5E1">mdi-file-document-outline</v-icon>
          <p class="empty-title">Renewal not found</p>
          <p class="empty-subtitle">Please go back and select a renewal from the list.</p>
          <v-btn color="primary" outlined class="tw-mt-4" @click="$router.push({ name: 'AgentRenewals' })">
            Back to Renewals
          </v-btn>
        </div>

      </div>
    </div>

    <!-- Document Viewer Modal -->
    <v-dialog
      v-model="docViewerVisible"
      max-width="1100"
      content-class="doc-viewer-dialog"
      @click:outside="closeDocViewer"
    >
      <v-card class="doc-viewer-card">
        <div class="doc-viewer-header">
          <div class="tw-flex tw-items-center tw-gap-3">
            <v-icon color="#E53E3E" size="22">mdi-file-pdf-box</v-icon>
            <div>
              <h3 class="doc-viewer-title">{{ docViewerTitle }}</h3>
              <p class="doc-viewer-subtitle">Document Preview</p>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <v-btn v-if="docViewerUrl" small outlined color="primary" @click="openDocFull">
              <v-icon x-small left>mdi-open-in-new</v-icon>
              Open Full
            </v-btn>
            <v-btn icon small @click="closeDocViewer">
              <v-icon size="20" color="#718096">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>
        <div v-if="docViewerLoading" class="doc-viewer-loading">
          <v-progress-circular indeterminate color="#00AEEF" size="48"></v-progress-circular>
          <p class="tw-mt-4" style="color:#94A3B8; font-size:0.9rem;">Loading document…</p>
        </div>
        <div v-else-if="docViewerUrl" class="doc-viewer-frame-wrap">
          <iframe :src="docViewerUrl" class="doc-viewer-iframe" frameborder="0"></iframe>
        </div>
      </v-card>
    </v-dialog>
  </agent-default>
</template>

<script>
import axios from 'axios';
import AgentDefault from '@/components/layout/AgentDefault';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import {
  loadRenewalFromSession,
  saveRenewalToSession,
  canRenewNow,
  isMotorRenewal,
  isComprehensiveRenewal,
  mergeGisRenewalBatchDetail,
  policyToSlug,
  formatRenewalDate,
  formatRenewalCurrency,
} from '@/utils/agentRenewal';
import { fetchGisRenewalBatchDetail } from '@/services/renewalApi';
import {
  displayRenewalBenefitRows,
  formatRenewalBenefitName,
  renewalBenefitLimitLabel,
} from '@/utils/motorBenefits';

export default {
  name: 'AgentRenewalDetails',
  components: { AgentDefault },
  data() {
    return {
      loading: true,
      renewal: null,
      renewalType: 'pending',
      policyDocs: [],
      policyDocsLoading: false,
      policyDocsError: null,
      docViewerVisible: false,
      docViewerUrl: '',
      docViewerTitle: '',
      docViewerLoading: false,
    };
  },
  computed: {
    policySlug() {
      return this.$route.params.policyNo || '';
    },
    risks() {
      return Array.isArray(this.renewal?.risks) ? this.renewal.risks : [];
    },
    showRenewNow() {
      return canRenewNow(this.renewal, this.renewalType);
    },
    isComprehensive() {
      return isComprehensiveRenewal(this.renewal);
    },
    benefitRows() {
      return displayRenewalBenefitRows(this.risks);
    },
  },
  watch: {
    policySlug: { immediate: true, handler() { this.loadRenewal(); } },
  },
  mounted() {
    this.loadRenewal();
  },
  methods: {
    async loadRenewal() {
      this.loading = true;
      try {
        const { renewal: sessionRenewal, renewalType } = loadRenewalFromSession(this.policySlug);
        let renewal = sessionRenewal;
        this.renewalType = renewalType;

        const batchNo = renewal?.effectiveBatchNo || renewal?.batchNo;
        if (renewal && batchNo) {
          const detailRes = await fetchGisRenewalBatchDetail(batchNo);
          if (detailRes.success && detailRes.data) {
            renewal = mergeGisRenewalBatchDetail(renewal, detailRes.data);
            saveRenewalToSession(renewal, renewalType);
          }
        }

        if (renewal && !isMotorRenewal(renewal)) {
          this.$notify.error('Only motor policies can be renewed in the portal.');
          this.renewal = null;
          this.$router.replace({ name: 'AgentRenewals' });
          return;
        }
        this.renewal = renewal;
        if (this.renewal?.policyNo) {
          this.fetchPolicyDocuments(this.renewal.policyNo);
        }
      } finally {
        this.loading = false;
      }
    },

    startRenewal() {
      if (!this.showRenewNow || !this.renewal?.policyNo) return;
      if (!isMotorRenewal(this.renewal)) {
        this.$notify.error('Only motor policies can be renewed in the portal.');
        return;
      }
      const slug = policyToSlug(this.renewal.policyNo);
      saveRenewalToSession(this.renewal, this.renewalType);
      trackAgentInteraction(this, 'renewal_renew_now_click', {
        policy_number: this.renewal.policyNo,
        renewal_type: this.renewalType,
      });
      this.$router.push({ name: 'AgentRenewPolicy', params: { policyNo: slug } });
    },

    async fetchPolicyDocuments(policyNo) {
      if (!policyNo) return;
      this.policyDocsLoading = true;
      this.policyDocsError = null;
      try {
        const res = await axios.get(
          `/customer/document-api/v1/getNBDocByPolicyNo?policyNo=${policyNo}`,
          { validateStatus: () => true },
        );
        if (res.status === 200 && Array.isArray(res.data)) {
          const ALLOWED = ['.pdf', '.doc', '.docx'];
          this.policyDocs = res.data.filter(
            (doc) => ALLOWED.includes((doc.mimeType || '').toLowerCase()),
          );
        } else {
          this.policyDocsError = 'Unable to load documents.';
        }
      } catch {
        this.policyDocsError = 'An error occurred fetching documents.';
      } finally {
        this.policyDocsLoading = false;
      }
    },

    openDocument(doc) {
      const docId = typeof doc === 'object' ? doc.id : doc;
      const docName = typeof doc === 'object' ? (doc.actualName || `Document #${docId}`) : `Document #${docId}`;
      trackAgentInteraction(this, 'renewal_details_open_document', {
        document_id: docId,
        document_name: docName,
      });
      this.docViewerTitle = docName;
      this.docViewerLoading = false;
      const basePath = process.env.NODE_ENV === 'production' ? '/api' : '';
      this.docViewerUrl = `${basePath}/customer/document-api/v1/viewUWDocs/${docId}`;
      this.docViewerVisible = true;
    },

    closeDocViewer() {
      this.docViewerVisible = false;
      this.docViewerUrl = '';
    },

    openDocFull() {
      if (this.docViewerUrl) {
        trackAgentInteraction(this, 'renewal_details_open_document_full', {
          document_name: this.docViewerTitle || 'unknown',
        });
        window.open(this.docViewerUrl, '_blank', 'noopener,noreferrer');
      }
    },

    formatDate: formatRenewalDate,
    formatCurrency: formatRenewalCurrency,
    formatBenefitName: formatRenewalBenefitName,
    benefitLimitLabel(row) {
      return renewalBenefitLimitLabel(row, formatRenewalCurrency);
    },
  },
  metaInfo: { title: 'Renewal Details' },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; gap: 16px;
}
.page-title {
  font-size: 1.35rem; font-weight: 800; color: #1E4276; margin: 0 0 4px 0;
}
.renew-now-btn {
  font-weight: 700 !important;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 14px rgba(0, 174, 239, 0.35);
}

/* ── Content Cards ── */
.detail-card {
  background: white; border-radius: 16px; border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06); padding: 24px 32px;
}
.detail-section-title {
  font-size: 1.05rem; font-weight: 700; color: #1E4276;
  margin: 0 0 20px 0; padding-bottom: 12px; border-bottom: 2px solid #E8EDF2;
}
.detail-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 24px;
}
.detail-item {
  display: flex; flex-direction: column; gap: 6px;
}
.detail-label {
  font-size: 0.825rem; font-weight: 600; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.05em;
}
.detail-value {
  font-size: 1rem; font-weight: 600; color: #1E4276;
}

/* ── Risks Table ── */
.risks-table-wrap {
  overflow-x: auto; -webkit-overflow-scrolling: touch; border: 1px solid #E2E8F0; border-radius: 10px;
}
.risks-table {
  width: 100%; min-width: 600px; border-collapse: collapse;
}
.risks-table th {
  text-align: left; padding: 12px 16px; font-size: 0.75rem; font-weight: 700;
  color: #64748B; text-transform: uppercase; background: #F8FAFC; border-bottom: 2px solid #E2E8F0;
}
.risks-row { border-bottom: 1px solid #F1F5F9; }
.risks-row:last-child { border-bottom: none; }
.risks-table td {
  padding: 14px 16px; font-size: 0.9rem; color: #334155; vertical-align: middle;
}
.empty-risks {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px 20px; color: #94A3B8; font-size: 0.95rem;
}
.empty-risks p { margin: 12px 0 0 0; text-align: center; color: inherit; }

@media (max-width: 640px) {
  .page-title { font-size: 1.2rem; }
  .detail-card { padding: 18px 16px; border-radius: 14px; }
  .detail-section-title { margin-bottom: 14px; padding-bottom: 10px; }
  .detail-grid { grid-template-columns: 1fr 1fr; gap: 14px 12px; }
  .detail-label { font-size: 0.68rem; }
  .detail-value { font-size: 0.9rem; overflow-wrap: anywhere; }
  .risks-table { min-width: 0; }
  .risks-table th { padding: 10px; font-size: 0.68rem; white-space: nowrap; }
  .risks-table td { padding: 10px; font-size: 0.82rem; white-space: nowrap; }
  .empty-risks { padding: 24px 12px; }
}
.benefits-hint {
  font-size: 0.875rem; color: #64748B; margin: -8px 0 16px;
}
.benefit-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.benefit-stack-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  background: #F8FAFC;
}
.benefit-stack-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.benefit-stack-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1E4276;
}
.benefit-stack-vehicle {
  font-size: 0.75rem;
  color: #94A3B8;
}
.benefit-stack-meta {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748B;
  white-space: nowrap;
}

/* ── Empty State ── */
.empty-state {
  display: flex; flex-direction: column; align-items: center; text-align: center;
}
.empty-title {
  font-size: 1.1rem; font-weight: 700; color: #1E4276; margin: 16px 0 6px;
}
.empty-subtitle {
  font-size: 0.875rem; color: #94A3B8; margin: 0;
}

/* ── Policy Docs Grid ── */
.docs-count-badge {
  background: #EBF8FF; color: #2B6CB0; font-size: 0.75rem; font-weight: 700;
  padding: 3px 10px; border-radius: 20px;
}
.policy-docs-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px;
}
.policy-doc-card {
  border: 1.5px solid #E2E8F0; border-radius: 10px; padding: 14px;
  cursor: pointer; transition: all 0.2s; display: flex; flex-direction: column; gap: 8px;
}
.policy-doc-card:hover {
  border-color: #00AEEF; box-shadow: 0 4px 12px rgba(0,174,239,0.12); transform: translateY(-2px);
}
.policy-doc-icon-wrap { display: flex; align-items: center; }
.policy-doc-body { flex: 1; }
.policy-doc-name {
  font-size: 0.825rem; font-weight: 700; color: #1E4276; margin: 0 0 4px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.policy-doc-type {
  font-size: 0.72rem; font-weight: 600; background: #EBF8FF; color: #2B6CB0;
  padding: 2px 8px; border-radius: 10px; display: inline-block;
}
.policy-doc-footer {
  display: flex; justify-content: space-between; align-items: center; margin-top: 4px;
}
.policy-doc-date { font-size: 0.72rem; color: #94A3B8; }

/* ── Doc Viewer Modal ── */
.doc-viewer-card {
  border-radius: 14px !important; display: flex; flex-direction: column;
  height: 90vh; max-height: 90vh; overflow: hidden;
}
.doc-viewer-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; background: #FAFBFC; flex-shrink: 0;
}
.doc-viewer-title {
  font-size: 0.95rem; font-weight: 700; color: #1E4276; margin: 0;
}
.doc-viewer-subtitle {
  font-size: 0.72rem; color: #94A3B8; margin: 0;
}
.doc-viewer-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center; height: 500px;
}
.doc-viewer-frame-wrap {
  flex: 1; overflow: hidden; display: flex;
}
.doc-viewer-iframe {
  width: 100%; height: 100%; min-height: 600px; border: none; display: block; flex: 1;
}
</style>

<style>
.doc-viewer-dialog {
  border-radius: 14px !important; overflow: hidden; margin: 24px auto !important;
}
</style>
